import { MapContainer, TileLayer, Marker, Popup, Tooltip } from "react-leaflet";
import L from "leaflet";
import { Box, Typography } from "@mui/material";
import { useEffect, useMemo, useRef } from "react";
import "leaflet/dist/leaflet.css";
import useLocalization from "../../hooks/useLocalization";

// Fix default marker icons in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Default icon (blue)
const defaultIcon = new L.Icon({
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Orange icon (selected in multi-select mode)
const selectedIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-orange.png",
  iconRetinaUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  iconSize: [35, 46],
  iconAnchor: [17, 46],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Red icon (hovered)
const hoveredIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
  iconRetinaUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  iconSize: [35, 46],
  iconAnchor: [17, 46],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Dark red icon (selected AND hovered)
const selectedHoveredIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
  iconRetinaUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  iconSize: [35, 46],
  iconAnchor: [17, 46],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Black icon for users (reps)
const userIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-black.png",
  iconRetinaUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-black.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  iconSize: [35, 46],
  iconAnchor: [17, 46],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const LocationsMap = ({
  locations = [],
  users = [],
  mode = "location", // "location" or "call"
  selectedLocationId = null,
  hoveredLocationId = null,
  onSelectLocation = null,
  showTooltips = true,
  selectedIds = new Set(),
  onToggleSelection,
}) => {
  const { getLabel } = useLocalization();
  const mapRef = useRef(null);
  const isMountedRef = useRef(true);
  const isMultiSelectMode = Boolean(onToggleSelection) && selectedIds.size >= 0;
  // Filter locations with valid coordinates
  const validLocations = locations.filter(
    (loc) =>
      loc.latitude !== undefined &&
      loc.latitude !== null &&
      loc.longitude !== undefined &&
      loc.longitude !== null,
  );

  const validUsers = users.filter(
    (user) =>
      user.latitude !== undefined &&
      user.latitude !== null &&
      user.longitude !== undefined &&
      user.longitude !== null,
  );

  const hasLocations = validLocations.length > 0;
  const allPoints = [...validLocations, ...validUsers];
  const hasPoints = allPoints.length > 0;

  const coordinates = useMemo(
    () => allPoints.map((point) => [point.latitude, point.longitude]),
    [allPoints],
  );

  // Calculate center point
  const avgLat = hasPoints
    ? allPoints.reduce((sum, point) => sum + point.latitude, 0) /
      allPoints.length
    : 0;
  const avgLng = hasPoints
    ? allPoints.reduce((sum, point) => sum + point.longitude, 0) /
      allPoints.length
    : 0;

  const centerLat = Number.isFinite(avgLat) ? avgLat : 0;
  const centerLng = Number.isFinite(avgLng) ? avgLng : 0;

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current || !isMountedRef.current) return;
    const timer = setTimeout(() => {
      if (isMountedRef.current && mapRef.current) {
        mapRef.current.invalidateSize();
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [locations.length]);

  useEffect(() => {
    if (!mapRef.current || !hasPoints || !isMountedRef.current) return;
    const timer = setTimeout(() => {
      if (isMountedRef.current && mapRef.current) {
        const bounds = L.latLngBounds(coordinates);
        mapRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [coordinates, hasPoints]);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: 420,
        "& .leaflet-container": {
          width: "100%",
          height: "100%",
        },
      }}
    >
      {!hasPoints && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            backgroundColor: "rgba(245, 245, 245, 0.75)",
          }}
        >
          <Typography color="textSecondary">
            {getLabel("noLocationsWithCoordinates")}
          </Typography>
        </Box>
      )}
      <MapContainer
        center={[centerLat, centerLng]}
        zoom={hasPoints ? 12 : 2}
        minZoom={2}
        maxZoom={18}
        zoomAnimation={false}
        style={{ width: "100%", height: "100%", borderRadius: "4px" }}
        whenReady={(event) => {
          if (isMountedRef.current) {
            mapRef.current = event.target;
            event.target.invalidateSize();
          }
        }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />

        {validLocations.map((location) => {
          const isSelected = selectedIds.has(location.id);
          const isHovered = hoveredLocationId === location.id;

          // Determine icon based on selection/hover state
          let icon = defaultIcon;
          if (isMultiSelectMode) {
            if (isSelected && isHovered) {
              icon = selectedHoveredIcon;
            } else if (isSelected) {
              icon = selectedIcon;
            } else if (isHovered) {
              icon = hoveredIcon;
            }
          } else {
            // Original behavior for non-multi-select
            if (
              selectedLocationId === location.id ||
              hoveredLocationId === location.id
            ) {
              icon = selectedIcon;
            }
          }

          return (
            <Marker
              key={location.id}
              position={[location.latitude, location.longitude]}
              icon={icon}
              eventHandlers={{
                click: () => {
                  // Empty function - pin click does nothing
                },
              }}
            >
              {showTooltips && (
                <Tooltip
                  direction="top"
                  offset={[0, -20]}
                  opacity={0.9}
                >
                  <Box sx={{ minWidth: 180 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: "bold" }}
                    >
                      {location.name}
                    </Typography>
                    <Typography variant="caption">
                      #{location.locationNumber || location.number}
                    </Typography>
                    <Typography
                      variant="caption"
                      display="block"
                    >
                      {location.address?.streetAddress}
                    </Typography>
                  </Box>
                </Tooltip>
              )}
              <Popup>
                <Box sx={{ pl: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold" }}
                  >
                    {location.name}
                  </Typography>
                  <Typography variant="caption">
                    #{location.locationNumber || location.number}
                  </Typography>
                  <Typography
                    variant="caption"
                    display="block"
                  >
                    {location.address?.streetAddress}
                  </Typography>
                  {mode !== "location" && (
                    <>
                      <br />
                      <Typography
                        variant="caption"
                        sx={{ mt: 1, display: "block", color: "#666" }}
                      >
                        {location.calls?.length || 0} call
                        {location.calls?.length !== 1 ? "s" : ""}
                      </Typography>
                    </>
                  )}
                </Box>
              </Popup>
            </Marker>
          );
        })}

        {validUsers.map((user) => (
          <Marker
            key={`user-${user.id}`}
            position={[user.latitude, user.longitude]}
            icon={userIcon}
          >
            {showTooltips && (
              <Tooltip
                direction="top"
                offset={[0, -20]}
                opacity={0.9}
              >
                <Box sx={{ minWidth: 180 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold" }}
                  >
                    👤 {user.legalName || user.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    display="block"
                  >
                    Assigned Rep
                  </Typography>
                </Box>
              </Tooltip>
            )}
            <Popup>
              <Box sx={{ pl: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: "bold" }}
                >
                  👤 {user.legalName || user.name}
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ color: "#2e7d32" }}
                >
                  Assigned Representative
                </Typography>
                <br />
                <Typography variant="caption">{user.address}</Typography>
              </Box>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </Box>
  );
};

export default LocationsMap;
